import * as _ from 'lodash';
import { CampanhaSearchView } from '../../campanha-search-view';
import { CampanhaSearchViewStatus } from '../../campanha-search-view-status';
import { CampanhaSearch } from '../../campanha-search';
import { CampanhaPipelineList } from './campanha-pipeline-list';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { campanhaStatusNaoConfirmada, campanhaStatusEmAnalise, campanhaStatusEmAndamento, campanhaStatusEncerrada } from '../../status';
import { CampanhaResultado, createResultado, sumResultado } from '../../resultado';

export class CampanhaPipeline {
    private pipelineHeight: number;
    private pipelineCardHeight = 35; // 35px é o tamanho médio dos cards dentro do pipeline
    private searchLimit = 20;
    lists: CampanhaPipelineList[] = [];
    isDragging = false;
    quantidadeCampanhas: number;
    resultado: CampanhaResultado;

    /** List do status da campanha que possui alguma campanha sendo editada */
    private listEditing: CampanhaPipelineList;
    /** Campanha que está sendo editada */
    private entityEditing: CampanhaSearch;
    /** Evento de drop do pipeline **/
    private dropEvent: CdkDragDrop<CampanhaSearch[]>;

    /** Ponto de Equilíbrio do ROI */
    roiPontoEquilibrio: number;

    constructor(searchView: CampanhaSearchView, pipelineHeight: number, roiPontoEquilibrio: number) {
        this.pipelineHeight = pipelineHeight;
        this.roiPontoEquilibrio = roiPontoEquilibrio;
        this.lists = this.createLists(searchView.statuses ?? []);
        this.quantidadeCampanhas = searchView.total.quantidadeCampanhas ?? 0;
        this.resultado = searchView.total.resultado;
    }

    /** Obtem o limit inicial para as pesquisas através do calculo de quantos cards cabem dentro do height do pipeline */
    private getSearchLimit(): number {
        // Dividindo o height do pipeline pelo height do pipeline card
        const limit = Math.round(this.pipelineHeight / this.pipelineCardHeight);

        // Se o valor calculado do limit inicial for maior que o limit da pesquisa, retornar o limit calculado para a pesquisa inicial, se não, retorna o próprio limit da pesquisa
        return limit > this.searchLimit ? limit : this.searchLimit;
    }

    private createLists(statuses: CampanhaSearchViewStatus[]): CampanhaPipelineList[] {
        const lists = [];

        statuses?.forEach(status => {
            const list = new CampanhaPipelineList(status, this.getSearchLimit());
            lists.push(list);
        });

        return lists;
    }

    dropEntity(fromListId: string, toListId: string, entity: CampanhaSearch) {
        try {
            if (fromListId) {
                const listFrom = this.getListById(fromListId);
                if (listFrom) {
                    listFrom.total.removeEntity(entity, this.roiPontoEquilibrio);
                }
            }

            if (toListId) {
                const listTo = this.getListById(toListId);
                if (listTo) {
                    listTo.total.addEntity(entity, this.roiPontoEquilibrio);
                }
            }

            this.calcularTotal();
        } catch (error) {
            throw new Error('Ocorreu um erro recalcular os totais dos status das campanhas. Atualize a página para ver os valores atualizados');
        }
    }

    calcularTotal() {
        this.quantidadeCampanhas = 0;
        this.resultado = createResultado();
        this.lists?.forEach(list => {
            // Não considerando status Reprovada
            if (!list.isStatusReprovada) {
                this.quantidadeCampanhas += list.total.quantidadeCampanhas;
                this.resultado = sumResultado(this.resultado, list.total.resultado, this.roiPontoEquilibrio);
            }
        });
    }

    getListById(id: string): CampanhaPipelineList {
        return this.lists.find(list => list.id == id);
    }

    /** Retorna a list do status Não Confirmada */
    getListNaoConfirmada(): CampanhaPipelineList {
        return this.getListById(campanhaStatusNaoConfirmada.id.toString());
    }

    /** Retorna a list do status Em Análise */
    getListEmAnalise(): CampanhaPipelineList {
        return this.getListById(campanhaStatusEmAnalise.id.toString());
    }

    /** Retorna a list do status Em Andamento */
    getListEmAndamento(): CampanhaPipelineList {
        return this.getListById(campanhaStatusEmAndamento.id.toString());
    }

    /** Retorna a list do status Encerrada */
    getListEncerrada(): CampanhaPipelineList {
        return this.getListById(campanhaStatusEncerrada.id.toString());
    }

    setListEditing(list: CampanhaPipelineList) {
        this.listEditing = list;
    }

    getListEditing(): CampanhaPipelineList {
        return this.listEditing;
    }

    setDropEvent(dropEvent: CdkDragDrop<CampanhaSearch[]>) {
        this.dropEvent = dropEvent;
    }

    getDropEvent(): CdkDragDrop<CampanhaSearch[]> {
        return this.dropEvent;
    }

    isSameList(entity: CampanhaSearch): boolean {
        if (!entity) {
            return false;
        }

        return entity.status == this.entityEditing.status;
    }

    setEntityEditing(entity: CampanhaSearch) {
        this.entityEditing =  _.cloneDeep(entity);
    }

    getEntityEditing(): CampanhaSearch {
        return this.entityEditing;
    }

    /** Adiciona uma campanha */
    addOneEntity(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        // Obtendo a list
        const list = this.getListById(entity.status.toString());
        // Adicionando a campanha
        list?.addOneEntity(entity, this.roiPontoEquilibrio);
        // Recalculando totais
        this.calcularTotal();
    }

    /** Remove uma campanha */
    removeOneEntity(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }
        // Obtendo a list
        const list = this.getListById(entity.status.toString());
        // Removendo a campanha
        list?.removeOneEntity(entity.id, this.roiPontoEquilibrio);
        // Recalculando totais
        this.calcularTotal();
    }

    updateEntity(entity: CampanhaSearch) {
        this.listEditing.updateOneEntity(entity, this.entityEditing, this.roiPontoEquilibrio);
        this.listEditing = null;
        this.entityEditing = null;
        this.calcularTotal();
    }

    moveEntity(entity: CampanhaSearch) {
        if (!entity) {
            return;
        }

        // Primeiro removemos a entity da lista antiga
        this.listEditing.removeOneEntity(this.entityEditing.id, this.roiPontoEquilibrio);
        // Adicionando a entity na sua nova lista
        const list = this.getListById(entity.status.toString());
        list?.addOneEntity(entity, this.roiPontoEquilibrio);

        this.listEditing = null;
        this.entityEditing = null;
        this.calcularTotal();
    }
}
